import React, { createContext, ReactNode, useCallback, useContext } from 'react';

import { useModal } from '@futrli/shared-services';

import { COADisplayAccount } from '../../types';
import { CreateCoaGroupSidebar } from '../drawers/group/create-group-sidebar';
import { DeleteGroupDialog } from '../dialogs/delete-group-dialog';
import { DeleteSwitchRuleDialog } from '../dialogs/delete-switch-rule-dialog';
import { EditCoaGroupSidebar } from '../drawers/group/edit-group-sidebar';
import { ReclassifyAccountSidebar } from '../drawers/reclassify/reclassify-account-sidebar';
import { SwitchRuleSidebar } from '../drawers/switch-rule/switch-rule-sidebar';

interface CoaActionsContextValue {
  handleDeleteGroupClick: (coaRow: COADisplayAccount) => void;
  handleEditGroupClick: (coaRow: COADisplayAccount) => void;
  handleCreateGroupClick: () => void;
  handleReclassifyClick: (coaRow?: COADisplayAccount) => void;
  handleSwitchRuleClick: (coaRow: COADisplayAccount) => void;
  handleSwitchRuleDeleteClick: (coaRow: COADisplayAccount) => void;
}

const CoaActionsContext = createContext<CoaActionsContextValue>(({} as unknown) as CoaActionsContextValue);

export const CoaActionsProvider = ({ children }: { children: ReactNode }) => {
  const deleteGroupDialog = useModal();
  const deleteSwitchRuleDialog = useModal();

  const createGroupSidebar = useModal();
  const updateGroupSidebar = useModal();
  const reclassifySidebar = useModal();
  const switchRuleSidebar = useModal();

  const [coaRow, setCoaRow] = React.useState<COADisplayAccount>();
  const [accountToReclassify, setAccountToReclassify] = React.useState<COADisplayAccount>();
  const [switchRuleAccount, setSwitchRuleAccount] = React.useState<COADisplayAccount>();

  const handleDeleteGroupClick = useCallback(
    (coaRow: COADisplayAccount) => {
      setCoaRow(coaRow);
      deleteGroupDialog.open();
    },
    [deleteGroupDialog]
  );

  const handleSwitchRuleDeleteClick = useCallback(
    (coaRow: COADisplayAccount) => {
      setSwitchRuleAccount(coaRow);
      deleteSwitchRuleDialog.open();
    },
    [deleteSwitchRuleDialog]
  );

  const handleEditGroupClick = useCallback(
    (coaRow: COADisplayAccount) => {
      setCoaRow(coaRow);
      updateGroupSidebar.open();
    },
    [updateGroupSidebar]
  );

  const handleCreateGroupClick = useCallback(() => {
    createGroupSidebar.open();
  }, [createGroupSidebar]);

  const handleReclassifyClick = useCallback(
    (coaRow?: COADisplayAccount) => {
      setAccountToReclassify(coaRow);
      reclassifySidebar.open();
    },
    [reclassifySidebar]
  );

  const handleSwitchRuleClick = useCallback(
    (coaRow: COADisplayAccount) => {
      setSwitchRuleAccount(coaRow);
      switchRuleSidebar.open();
    },
    [switchRuleSidebar]
  );

  const value = {
    handleDeleteGroupClick,
    handleEditGroupClick,
    handleCreateGroupClick,
    handleReclassifyClick,
    handleSwitchRuleClick,
    handleSwitchRuleDeleteClick
  };

  return (
    <CoaActionsContext.Provider value={value}>
      <CreateCoaGroupSidebar isOpen={createGroupSidebar.isOpen} onClose={createGroupSidebar.close} />
      <ReclassifyAccountSidebar
        isOpen={reclassifySidebar.isOpen}
        onClose={reclassifySidebar.close}
        accountToReclassify={accountToReclassify}
      />
      {switchRuleAccount && (
        <>
          <SwitchRuleSidebar
            isOpen={switchRuleSidebar.isOpen}
            onClose={switchRuleSidebar.close}
            account={switchRuleAccount}
          />
          <DeleteSwitchRuleDialog
            coaRow={switchRuleAccount}
            isOpen={deleteSwitchRuleDialog.isOpen}
            onClose={deleteSwitchRuleDialog.close}
          />
        </>
      )}
      {coaRow && (
        <>
          <DeleteGroupDialog coaRow={coaRow} isOpen={deleteGroupDialog.isOpen} onClose={deleteGroupDialog.close} />
          <EditCoaGroupSidebar row={coaRow} isOpen={updateGroupSidebar.isOpen} onClose={updateGroupSidebar.close} />
        </>
      )}

      {children}
    </CoaActionsContext.Provider>
  );
};

export const useCoaActions = () => {
  const context = useContext(CoaActionsContext);
  if (!context) {
    throw new Error('useCoaActions must be used within a CoaActionsProvider');
  }
  return context;
};
